import { useEffect } from "react";
import { toast } from "sonner";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { getPlyxToken } from "@/lib/plyxApi";

/**
 * PlyxRipplesListener — global personal-inbox WebSocket.
 *
 * Mounted once at the Plyx app shell. While a user is signed in, this
 * holds a single WebSocket open to `/api/ws/plyx/user` so the server
 * can push personal notifications without per-page polling:
 *
 *   • {type: "ripple", kind: "likes", threshold: 100, creator_handle,
 *      caption, video_id}
 *     A creator the user follows just crossed a like milestone — pops
 *     a sonner toast with a tap-to-watch link.
 *
 *   • {type: "ready"} — handshake echo (ignored).
 *   • {type: "pong"} — keepalive response.
 *
 * Reconnection: if the socket drops we wait a backoff and retry. The
 * cleanup closes the socket so logging out instantly stops broadcasts
 * from being routed to the user's tab.
 */
const MAX_BACKOFF_MS = 30_000;

const formatThreshold = (n) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
};

const PlyxRipplesListener = () => {
  const { user } = usePlyxAuth();

  useEffect(() => {
    if (!user) return undefined;
    const token = getPlyxToken();
    if (!token) return undefined;

    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const wsBase = backend.replace(/^http/, "ws");
    const url = `${wsBase}/api/ws/plyx/user?token=${encodeURIComponent(token)}`;

    let ws;
    let alive = true;
    let backoff = 1000;
    let keepAliveId;

    const connect = () => {
      try {
        ws = new WebSocket(url);
      } catch {
        return;
      }
      ws.onopen = () => {
        backoff = 1000;
        // Keepalive ping so proxies don't reap an idle socket.
        keepAliveId = setInterval(() => {
          try { ws.send(JSON.stringify({ type: "ping" })); } catch { /* ignore */ }
        }, 25_000);
      };
      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); } catch { return; }
        if (msg?.type !== "ripple") return;
        // Render a tap-to-watch toast. Sonner's JSX support lets us
        // embed a Link so the user can jump straight to the clip.
        const threshold = formatThreshold(msg.threshold || 0);
        const handle = msg.creator_handle ? `@${msg.creator_handle}` : "Someone you follow";
        const headline = `${handle}'s video just hit ${threshold} likes ✨`;
        toast.success(headline, {
          description: msg.caption?.slice(0, 80) || "Tap to watch",
          duration: 7000,
          action: msg.video_id ? {
            label: "Watch",
            onClick: () => {
              // window.location for hard nav so the toast fires from
              // any route and lands the user on the player.
              window.location.href = `/plyx/v/${msg.video_id}`;
            },
          } : undefined,
        });
      };
      ws.onclose = () => {
        clearInterval(keepAliveId);
        if (!alive) return;
        setTimeout(connect, backoff);
        backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
      };
      ws.onerror = () => {
        try { ws.close(); } catch { /* ignore */ }
      };
    };

    connect();

    return () => {
      alive = false;
      clearInterval(keepAliveId);
      try { ws?.close(); } catch { /* ignore */ }
    };
  }, [user]);

  return null;
};

// eslint-disable-next-line no-unused-vars
export default PlyxRipplesListener;
